import React, { useEffect } from 'react';
import Page from '@Containers/Page';
import { Switch, useLocation, matchPath } from 'react-router-dom';
import ConnectedRoute from '@AppRoutes/ConnectedRoute';

import NewRelicService from '@Services/NewRelicService';
import RouteChangeTracking from '@Services/RouteChangeTracking';

import RetroSynthesisProjectsContainer from '@Pages/RetroSynthesis/RetroSynthesisProjects/RetroSynthesisProjectsContainer';
import DrawStructureContainer from '@Pages/RetroSynthesis/DrawStructure/DrawStructureContainer';
import RsRootInitializerContainer from '@Pages/RetroSynthesis/RetroSynthesisRoot/RsRootInitializerContainer';
import Tooltip, { overridePositionTopLeft, TooltipConstants } from '@Components/Tooltip';
import RsBreadcrumbsContainer from '@Pages/RetroSynthesis/RsBreadcrumbs/RsBreadcrumbsContainer';
import ProjectRoutesContainer from '@Pages/RetroSynthesis/ProjectRoutes/ProjectRoutesContainer';
import ProjectRouteContainer from '@Pages/RetroSynthesis/ProjectRoute';
import StructureOptionContainer from '@Pages/RetroSynthesis/StructureOption/StructureOptionContainer';
import { saveRsLocation } from '@Pages/RetroSynthesis/RetroSynthesisRoot/utils/rsSavedLocation';
import LinkOptionContainer from '@Pages/RetroSynthesis/LinkOption/LinkOptionContainer';
import { RsRoute } from '../routes';

import './RetroSynthesisRoot.scss';

// FR-3.4/3.5: My Projects and the Project routes list opt into the shared header-inside scroll
// root (mirrors Main.jsx's HEADER_INSIDE_SCROLL_ROOT_PATHS) so the app header scrolls away while
// the RT table toolbar + table header stay sticky. Draw (`DRAW_STRUCTURE`) and Tree view
// (`PROJECT_ROUTE`, an extra path segment beyond `PROJECT`) are excluded via `exact` matching and
// "remain as they are".
const HEADER_INSIDE_ROUTE_PATTERNS = [RsRoute.PROJECT, RsRoute.PROJECTS_LIST];

export default function RetroSynthesisRoot() {
  const location = useLocation();
  const isHeaderInsideRoute = HEADER_INSIDE_ROUTE_PATTERNS.some((path) => matchPath(location?.pathname, { path, exact: true }));

  // Save RS last visited location
  useEffect(() => {
    // Don't save location if it is root '/retrosynthesis'
    if (location && RsRoute.isRsLocation(location) && location.pathname !== RsRoute.ROOT) {
      NewRelicService.trackRouteChanges(location.pathname);
      saveRsLocation(location);
    }
  }, [location]);

  useEffect(() => {
    RouteChangeTracking.handleSkipLinkFocus();
  }, [location]);

  return (
    <Page
      withFooter={false}
      className="retro-synthesis-root"
      pageClassName={isHeaderInsideRoute ? undefined : 'hidden-overflow'}
      renderHeaderInsideScrollRoot={isHeaderInsideRoute}
      headerType="main">
      <StructureOptionContainer />
      <LinkOptionContainer />
      <RsBreadcrumbsContainer />
      <div className="retro-synthesis-root__content">
        <Switch>
          <ConnectedRoute path={RsRoute.DRAW_STRUCTURE} render={() => <DrawStructureContainer />} />
          <ConnectedRoute path={RsRoute.PROJECT_ROUTE} render={() => <ProjectRouteContainer />} />
          <ConnectedRoute path={RsRoute.PROJECT} render={() => <ProjectRoutesContainer />} />
          <ConnectedRoute path={RsRoute.PROJECTS_LIST} render={() => <RetroSynthesisProjectsContainer />} />
          <ConnectedRoute exact path={RsRoute.ROOT} render={() => <RsRootInitializerContainer />} />
        </Switch>
      </div>
      <Tooltip id={TooltipConstants.ids.Retrosynthesis} />
      <Tooltip id={TooltipConstants.ids.RetrosynthesisParams} offset={{ top: 100 }} place="left" />
      <Tooltip id={TooltipConstants.ids.RetrosynthesisStructureIcon} />
      <Tooltip id={TooltipConstants.ids.ResultStructureComponent} />
      <Tooltip id={TooltipConstants.ids.TreeViewToolbar} html overridePosition={overridePositionTopLeft} multiline />
    </Page>
  );
}
